// single line question type codeing

import React, { useState, useRef, useEffect } from "react";
import "bootstrap-icons/font/bootstrap-icons.css";
import FillInTheBlanks from "../FillInTheBlanks";
import App1 from "../App1";
import axios from "axios";
import TrueFalse from "./TrueFalse";

import {
  Dialog,
  DialogActions,
  Card, Box, CardContent,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
  Button,
  Chip,
  IconButton,
  Snackbar,
  Alert,
  Tooltip,
  Divider,
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import { useParams } from "react-router-dom";
import TagModal from "./TagModal";
import ImageOcclusion from "./ImageOcclusion";
import StyleIcon from "@mui/icons-material/Style";
import { useNavigate } from "react-router-dom";
import katex from "katex";
import "katex/dist/katex.min.css";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import sanitizeHtml from "sanitize-html";
import { FaChevronDown, FaChevronUp } from "react-icons/fa"; // For the arrow icons
import { useContext } from 'react';
import { ThemeContext } from '../context/ThemeContext';
import ExpandIcon from '@mui/icons-material/UnfoldLess';
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";


// Import the Clipboard module from Quill
const Clipboard = Quill.import("modules/clipboard");

class CustomClipboard extends Clipboard {
  onPaste(e) {
    e.preventDefault();
    const clipboardData = e.clipboardData || window.clipboardData;
    const html = clipboardData.getData("text/html");

    if (html) {
      const sanitizedHtml = sanitizeHtml(html, {
        allowedTags: sanitizeHtml.defaults.allowedTags.concat([
          "b", "i", "em", "strong", "span", "br", "u", "sub", "sup", "a", "p", "img"
        ]),
        allowedAttributes: false, // Strip all attributes except essential ones
      });

      // Insert the HTML at the cursor position
      const range = this.quill.getSelection();
      if (range) {
        this.quill.clipboard.dangerouslyPasteHTML(range.index, sanitizedHtml);
      }
    } else {
      const text = clipboardData.getData("text/plain");
      // Insert plain text as HTML with line breaks
      const formattedText = `<p>${text.replace(/\n/g, "<br>")}</p>`;
      const range = this.quill.getSelection();
      if (range) {
        this.quill.clipboard.dangerouslyPasteHTML(range.index, formattedText);
      }
    }
  }
}

// Register the custom clipboard
Quill.register("modules/clipboard", CustomClipboard);

// Custom Quill module to enforce character limit
function TextLimit(quill, options) {
  const CHAR_LIMIT = options.limit || 255;
  quill.on("text-change", function (delta, oldDelta, source) {
    if (source === "user") {
      const text = quill.getText().trim();
      if (text.length > CHAR_LIMIT) {
        quill.deleteText(CHAR_LIMIT, text.length - CHAR_LIMIT, "silent");
      }
    }
  });
  quill.clipboard.addMatcher(Node.TEXT_NODE, (node, delta) => {
    const text = quill.getText().trim();
    if (text.length + node.data.length > CHAR_LIMIT) {
      const allowedLength = CHAR_LIMIT - text.length;
      if (allowedLength <= 0) {
        return new Quill.imports.delta();
      }
      return new Quill.imports.delta().insert(node.data.substring(0, allowedLength));
    }
    return delta;
  });
}

// Register the custom module
Quill.register("modules/textLimit", TextLimit, true);

const Flashcard = () => {
  const [cards, setCards] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState("");
  const [currentAnswer, setCurrentAnswer] = useState("");
  const [currentExplanation, setCurrentExplanation] = useState("");
  const [options, setOptions] = useState([""]);
  const [correctOption, setCorrectOption] = useState("");
  const [isAnswer, setIsAnswer] = useState(false);
  const [isExplanation, setIsExplanation] = useState(false);
  const [questionType, setQuestionType] = useState("Fill in the Blanks");
  const [clozeAnswers, setClozeAnswers] = useState([]);
  const [userAnswers, setUserAnswers] = useState([]);
  const inputRef = useRef(null);
  const [collapsed, setCollapsed] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editCardIndex, setEditCardIndex] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteCardId, setDeleteCardId] = useState(null); // Rename to reflect it holds the card's ID
  const [editQuestion, setEditQuestion] = useState("");
  const [editAnswers, setEditAnswers] = useState("");
  const [editExplanation, setEditExplanation] = useState("");
  const [dropdownVisible, setDropdownVisible] = useState(false); // Controls dropdown visibility
  const [selectedOption, setSelectedOption] = useState(''); // State to track selected 
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");
  // Tag Modal States
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [tagCardIndex, setTagCardIndex] = useState(null);
  const [tagModalPosition, setTagModalPosition] = useState({ top: 0, left: 0 });
  const [collapsedQuestion, setCollapsedQuestion] = useState(false);
  const [collapsedAnswer, setCollapsedAnswer] = useState(false);
  const [collapsedExplanation, setCollapsedExplanation] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false); //  state for duplicate confirmation
  const navigate = useNavigate();
  const { subfolder_id } = useParams();
  const { mode, customColors } = useContext(ThemeContext);
  const [userGuideOpen, setUserGuideOpen] = useState(false);

  // State for character counts
  const [questionCharCount, setQuestionCharCount] = useState(0);
  const [answerCharCount, setAnswerCharCount] = useState(0);
  const [explanationCharCount, setExplanationCharCount] = useState(0);
  const [editQuestionCharCount, setEditQuestionCharCount] = useState(0);
  const [editAnswerCharCount, setEditAnswerCharCount] = useState(0);
  const [editExplanationCharCount, setEditExplanationCharCount] = useState(0);
  const [myPermissions, setMyPermissions] = useState({
    can_create_sub: true,
    can_read_sub: true,
    can_update_sub: true,
    can_delete_sub: true,
    can_start_flashcard: true,
    can_start_quiz: true,
    is_admin: false,
  });
  const CHAR_LIMIT = 255;

  const sanitizeRichText = (html = "") => {
    if (!html) return "";

    return sanitizeHtml(html, {
      allowedTags: [
        "p",
        "br",
        "strong",
        "em",
        "b",
        "i",
        "u",
        "s",
        "sub",
        "sup",
        "ol",
        "ul",
        "li",
        "span",
        "div",
      ],
      allowedAttributes: {
        span: ["style"],
        div: ["style"],
        p: ["style"],
      },
      allowedStyles: {
        "*": {
          color: [/.*/],
          "background-color": [/.*/],
          "font-size": [/.*/],
          "text-align": [/^left$/, /^right$/, /^center$/, /^justify$/],
        },
      },
      transformTags: {
        h1: "p",
        h2: "p",
        h3: "p",
        h4: "p",
        h5: "p",
        h6: "p",
      },
    });
  };


  // Gradient colors for charts and UI
  const gradientPrimary = "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)";
  const gradientLight = "linear-gradient(135deg, #f0f7ff 0%, #e0e7ff 100%)";
  const gradientDark = "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)";

  // Dynamic text and background colors
  const textColor = mode === "dark" ? "#E0E0E0" : mode === "custom" ? customColors?.text || "#222222" : "#333333";
  const cardBg = mode === "dark" ? gradientDark : mode === "custom" ? customColors?.dashboardBg || gradientLight : gradientLight;
  const tooltipBgColor = mode === "dark" ? "#2A2A3C" : mode === "custom" ? customColors?.cardBg || "#ffffffcc" : "#ffffff";

  const apiUrl = process.env.REACT_APP_BASE_URL;
  const handleQuestionChange = (value, delta, source, editor) => {
    const text = editor.getText().trim();
    setQuestionCharCount(text.length);
    setCurrentQuestion(value);
  };

  const handleAnswerChange = (value, delta, source, editor) => {
    const text = editor.getText().trim();
    setAnswerCharCount(text.length);
    setCurrentAnswer(value);
  };

  const handleExplanationChange = (value, delta, source, editor) => {
    const text = editor.getText().trim();
    setExplanationCharCount(text.length);
    setCurrentExplanation(value);
  };

  const handleEditQuestionChange = (value, delta, source, editor) => {
    const text = editor.getText().trim();
    setEditQuestionCharCount(text.length);
    setEditQuestion(value);
  };

  const handleEditAnswerChange = (value, delta, source, editor) => {
    const text = editor.getText().trim();
    setEditAnswerCharCount(text.length);
    setEditAnswers(value);
  };

  const handleEditExplanationChange = (value, delta, source, editor) => {
    const text = editor.getText().trim();
    setEditExplanationCharCount(text.length);
    setEditExplanation(value);
  };

  useEffect(() => {
    if (subfolder_id) {
      fetchFlashcardsFromServer();
    }
  }, [subfolder_id]);

  const toggleQuestion = () => setCollapsedQuestion(!collapsedQuestion);
  const toggleAnswer = () => setCollapsedAnswer(!collapsedAnswer);
  const toggleExplanation = () =>
    setCollapsedExplanation(!collapsedExplanation);

  const fetchFlashcardsFromServer = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;

      // Retrieve the access token from session storage
      const token = sessionStorage.getItem('authToken');

      // Make the API call with the Authorization header
      const response = await fetch(`${apiUrl}/subfolder/${subfolder_id}/sub/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`, // Include the token in the Authorization header
          'Content-Type': 'application/json', // Optional, depending on API requirements
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch data: ${response.statusText}`);
      }

      const data = await response.json();

      const fetchedCards = data.map((item) => ({
        id: item.id, // Ensure each card has an id
        question: item.statement,
        question_type: item.question_type,
        answer: item.answers,
        explanation: item.explanation,
        expanded: false, // Set expanded to false by default
        tags: item.tags, // Store the entire tags array
      }));

      setCards(fetchedCards);
      resetFields();
    } catch (error) {
      console.error('Failed to fetch flashcards from server', error);
    }
  };


  const toggleCollapse = () => {
    setCollapsed(!collapsed);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  const showSnackbar = (message, severity = "error") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };


  const handleClozeAnswerChange = (index, value) => {
    const newClozeAnswers = [...clozeAnswers];
    newClozeAnswers[index] = value;
    setClozeAnswers(newClozeAnswers);
  };

  const addClozeField = () => {
    setClozeAnswers([...clozeAnswers, ""]);
  };
  useEffect(() => {
    const fetchMyPermissions = async () => {
      try {
        const token = sessionStorage.getItem("authToken");
        const response = await axios.get(`${apiUrl}/user/permissions/`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const {
          can_create_sub,
          can_read_sub,
          can_update_sub,
          can_delete_sub,
          // can_tag_sub,
          can_start_flashcard,
          can_start_quiz,
        } = response.data.permissions || {};
        setMyPermissions({
          can_create_sub: can_create_sub || false,
          can_read_sub: can_read_sub || false,
          can_update_sub: can_update_sub || false,
          can_delete_sub: can_delete_sub || false,
          // can_tag_sub: can_tag_sub || false,
          can_start_flashcard: can_start_flashcard || false,
          can_start_quiz: can_start_quiz || false,
          is_admin: false,
        });
      } catch (error) {
        console.error("Failed to fetch permissions:", error);
        showSnackbar("Failed to fetch permissions. Please try again.", "error");
        setMyPermissions({
          can_create_sub: false,
          can_read_sub: false,
          can_update_sub: false,
          can_delete_sub: false,
          // can_tag_sub: false,
          can_start_flashcard: false,
          can_start_quiz: false,
          is_admin: false,
        });
      }
    };

    const userRole = sessionStorage.getItem("userRole");
    if (userRole === "admin") {
      // Skip API call for admin and set all permissions to true
      setMyPermissions({
        can_create_sub: true,
        can_read_sub: true,
        can_update_sub: true,
        can_delete_sub: true,
        // can_tag_sub: true,
        can_start_flashcard: true,
        can_start_quiz: true,
        is_admin: true,
      });
    } else {
      // Fetch permissions for non-admin users
      fetchMyPermissions();
    }

    if (subfolder_id && (myPermissions.can_read_sub || myPermissions.is_admin)) {
      fetchFlashcardsFromServer();
    } else if (!myPermissions.can_read_sub && !myPermissions.is_admin) {
      showSnackbar("You don't have permission to view SUB flashcards.", "warning");
    }
  }, [subfolder_id, myPermissions.can_read_sub, myPermissions.is_admin]);

  const addFlashcardToServer = async (question, answer, explanation) => {
    if (!myPermissions.can_create_sub && !myPermissions.is_admin) {
      showSnackbar("You don't have permission to create a SUB flashcard.", "warning");
      return;
    }
    const questionHtml = sanitizeRichText(currentQuestion);
    const answerHtml = sanitizeRichText(currentAnswer);
    const explanationHtml = sanitizeRichText(currentExplanation || "");

    const payload = {
      statement: questionHtml,
      answers: answerHtml,
      explanation: explanationHtml,
      question_type: "SUB",
      tags: [],
    };
    const token = sessionStorage.getItem("authToken");

    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(`${apiUrl}/subfolder/${subfolder_id}/sub/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Failed to add flashcard to server");
      }

      const addedFlashcard = await response.json();
      console.log("Flashcard added:", addedFlashcard);
      showSnackbar("Flashcard added successfully!", "success");
      resetFields();
      fetchFlashcardsFromServer();
    } catch (error) {
      console.error("Failed to add flashcard to server", error);
      showSnackbar("Failed to add flashcard, please try again.", "error");
    }
  };



  const handleKeyDown = async (e) => {
    if (e.key === 'ArrowRight' && !isAnswer && !isExplanation) {
      e.preventDefault();
      setIsAnswer(true);
      setIsExplanation(false);
      setCurrentAnswer(''); // Reset the answer field
      if (inputRef.current) {
        inputRef.current.focus();
      }
    } else if (e.key === 'Enter' && isAnswer && !isExplanation) {
      e.preventDefault();
      if (currentQuestion.trim() && currentAnswer.trim()) {
        setIsAnswer(false);
        setIsExplanation(true);
        setCurrentExplanation(''); // Reset the explanation field
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }
    } else if (e.key === 'Enter' && isExplanation) {
      e.preventDefault();
      await handleAddFlashcard(); // Call the new function to handle submission
    }
  };

  const handleAddFlashcard = async () => {
    if (!myPermissions.can_create_sub && !myPermissions.is_admin) {
      showSnackbar(
        "You don't have permission to create a SUB flashcard.",
        "warning"
      );
      return;
    }
    if (!currentQuestion.trim() || !currentAnswer.trim()) {
      showSnackbar(
        "Please fill in the question and answer fields before adding a flashcard.",
        "warning"
      );
      return;
    }

    // ← NEW: Compare rich text (preserves formatting meaning)
    const questionForComparison = sanitizeRichText(currentQuestion).trim();

    const isDuplicate = cards.some((card) => {
      const existingQuestion = sanitizeRichText(card.question).trim();
      return existingQuestion === questionForComparison;
    });

    if (isDuplicate) {
      setDuplicateDialogOpen(true);
      return;
    }

    const explanationToSend = currentExplanation.trim() || "";

    try {
      // Now send raw Quill HTML (will be sanitized properly inside addFlashcardToServer)
      await addFlashcardToServer(
        currentQuestion,
        currentAnswer,
        explanationToSend
      );

      // Keep rich HTML in local state too
      setCards([
        ...cards,
        {
          question: currentQuestion, // ← Keep rich HTML
          answer: myPermissions.can_read_sub ? currentAnswer : "",
          explanation: myPermissions.can_read_sub
            ? currentExplanation || ""
            : "",
          questionType,
          expanded: false,
        },
      ]);

      showSnackbar("Flashcard added successfully!", "success");
      resetFields();
    } catch (error) {
      console.error("Failed to add flashcard", error);
      showSnackbar("Failed to add flashcard, please try again.", "error");
    }
  };


  const handleConfirmDuplicate = async () => {
    if (!myPermissions.can_create_sub && !myPermissions.is_admin) {
      showSnackbar("You don't have permission to create a SUB flashcard.", "warning");
      setDuplicateDialogOpen(false);
      return;
    }
    const explanationToSend = currentExplanation.trim() || "";
    try {
      await addFlashcardToServer(currentQuestion, currentAnswer, explanationToSend);
      setCards([
        ...cards,
        {
          question: currentQuestion.trim(),
          answer: currentAnswer.trim(),
          explanation: currentExplanation.trim() || "",
          questionType,
          expanded: false,
        },
      ]);
      setDuplicateDialogOpen(false);
      showSnackbar("Flashcard added successfully!", "success");
    } catch (error) {
      console.error("Failed to add flashcard", error);
      showSnackbar("Failed to add flashcard, please try again.", "error");
    }
  };

  const handleCancelDuplicate = () => {
    setDuplicateDialogOpen(false);
  };


  const resetFields = () => {
    setCurrentQuestion("");
    setCurrentAnswer("");
    setCurrentExplanation("");
    setIsAnswer(false);
    setIsExplanation(false);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };



  const handleToolbarClick = (type) => {
    setQuestionType(type);
    setCurrentQuestion("");
    setCurrentAnswer("");
    setCurrentExplanation("");
    setOptions([""]);
    setCorrectOption("");
    setIsAnswer(false);
    setIsExplanation(false);
    setClozeAnswers([]);
    setUserAnswers([]);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const addCloze = () => {
    if (currentQuestion.includes(currentAnswer)) {
      const clozeQuestion = currentQuestion.replace(currentAnswer, "_");
      setCurrentQuestion(clozeQuestion);
      setClozeAnswers([...clozeAnswers, currentAnswer]);
      setCurrentAnswer("");
    }
  };



  const handleUserAnswerChange = (index, value) => {
    const newUserAnswers = [...userAnswers];
    newUserAnswers[index] = value;
    setUserAnswers(newUserAnswers);
  };

  const toggleCardExpand = (index) => {
    const newCards = [...cards];
    newCards[index].expanded = !newCards[index].expanded;
    setCards(newCards);
  };

  const openEditModal = (index) => {
    if (!myPermissions.can_update_sub && !myPermissions.is_admin) {
      showSnackbar("You don't have permission to update a SUB flashcard.", "warning");
      return;
    }
    setEditCardIndex(index);
    setEditQuestion(cards[index].question);
    setEditAnswers(myPermissions.can_read_sub ? cards[index].answer : "");
    setEditExplanation(myPermissions.can_read_sub ? cards[index].explanation : "");
    setEditModalOpen(true);
  };

  const handleEditChange = (index, value) => {
    const updatedAnswers = [...userAnswers]; // Create a copy of the answers array
    updatedAnswers[index] = value; // Update the answer at the specified index
    setUserAnswers(updatedAnswers); // Update the state with the updated answers
  };

  const handleUpdateCard = async () => {
    if (!myPermissions.can_update_sub && !myPermissions.is_admin) {
      showSnackbar("You don't have permission to update a SUB flashcard.", "warning");
      return;
    }
    const questionHtml = sanitizeRichText(editQuestion);
    const answerHtml = sanitizeRichText(editAnswers);
    const explanationHtml = sanitizeRichText(editExplanation || "");

    const updatedCard = {
      statement: questionHtml,
      answers: answerHtml,
      explanation: explanationHtml,
      question_type: "SUB",
      tags: [],
    };

    try {
      const cardId = cards[editCardIndex].id;
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("authToken");
      const response = await axios.put(
        `${apiUrl}/subfolder/${subfolder_id}/sub/${cardId}/`,
        updatedCard,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const updatedCards = [...cards];
      updatedCards[editCardIndex] = {
        ...updatedCards[editCardIndex],
        question: editQuestion, // ← Keep the raw rich HTML from Quill
        answer: editAnswers,
        explanation: editExplanation || "",
      };
      setCards(updatedCards);
      setEditModalOpen(false);
      fetchFlashcardsFromServer();
      showSnackbar("Flashcard updated successfully!", "success");
    } catch (error) {
      console.error("Failed to update flashcard on server", error);
      showSnackbar("Failed to update flashcard, please try again.", "error");
    }
  };


  const handleCloseModal = () => {
    setEditModalOpen(false);
  };

  const openDeleteDialog = (id) => {
    if (!myPermissions.can_delete_sub && !myPermissions.is_admin) {
      showSnackbar("You don't have permission to delete a SUB flashcard.", "warning");
      return;
    }
    setDeleteCardId(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCard = async () => {
    if (!myPermissions.can_delete_sub && !myPermissions.is_admin) {
      showSnackbar("You don't have permission to delete a SUB flashcard.", "warning");
      return;
    }
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("authToken");
      const response = await axios.delete(
        `${apiUrl}/subfolder/${subfolder_id}/sub/${deleteCardId}/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const updatedCards = cards.filter((card) => card.id !== deleteCardId);
      setCards(updatedCards);
      setDeleteDialogOpen(false);
      showSnackbar("Flashcard deleted successfully!", "success");
    } catch (error) {
      console.error("Failed to delete flashcard from server", error);
      showSnackbar("Failed to delete flashcard, please try again.", "error");
    }
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  // Tag Modal Functions
  const openTagModal = (index, event) => {
    // if (!myPermissions.can_tag_sub && !myPermissions.is_admin) 
    //   {
    //   showSnackbar("You don't have permission to tag a SUB flashcard.", "warning");
    //   return;
    // }
    console.log('Opening TagModal:', { cardId: cards[index]?.id, questionType: cards[index]?.question_type, subfolderId: subfolder_id });
    const iconRect = event.target.getBoundingClientRect();
    setTagCardIndex(index);
    setTagModalPosition({
      top: iconRect.bottom + window.scrollY,
      left: iconRect.left + window.scrollX,
    });
    setTagModalOpen(true);
  };

  const handleTagAdded = (cardId, newTag) => {
    if (!cardId || !newTag || !newTag.name) {
      console.error('Invalid tag data:', { cardId, newTag });
      showSnackbar('Invalid tag data. Please try again.', 'error');
      return;
    }

    try {
      const updatedCards = cards.map((card) => {
        if (card.id === cardId) {
          return {
            ...card,
            tags: [...card.tags, { name: newTag.name, id: newTag.id || null }],
          };
        }
        return card;
      });
      setCards(updatedCards);
      setTagModalOpen(false);
    } catch (error) {
      console.error('Error updating local state in handleTagAdded:', error);
      showSnackbar('Failed to update tag locally: ' + error.message, 'error');
    }
  };

  const handleTagUpdated = (cardId, updatedTag) => {
    if (!cardId || !updatedTag || !updatedTag.id || !updatedTag.name) {
      console.error('Invalid tag update data:', { cardId, updatedTag });
      showSnackbar('Invalid tag update data. Please try again.', 'error');
      return;
    }

    try {
      const updatedCards = cards.map((card) => {
        if (card.id === cardId) {
          return {
            ...card,
            tags: card.tags.map((tag) =>
              tag.id === updatedTag.id ? { ...tag, name: updatedTag.name } : tag
            ),
          };
        }
        return card;
      });
      setCards(updatedCards);
    } catch (error) {
      console.error('Error updating local state in handleTagUpdated:', error);
      showSnackbar('Failed to update tag locally: ' + error.message, 'error');
    }
  };

  const handleTagDeleted = (cardId, tagId) => {
    if (!cardId || !tagId) {
      console.error('Invalid tag deletion data:', { cardId, tagId });
      showSnackbar('Invalid tag deletion data. Please try again.', 'error');
      return;
    }

    try {
      const updatedCards = cards.map((card) => {
        if (card.id === cardId) {
          return {
            ...card,
            tags: card.tags.filter((tag) => tag.id !== tagId),
          };
        }
        return card;
      });
      setCards(updatedCards);
    } catch (error) {
      console.error('Error updating local state in handleTagDeleted:', error);
      showSnackbar('Failed to delete tag locally: ' + error.message, 'error');
    }
  };

  // const handleIconClick = () => {
  //   const hardCodedText = "sub"; // replace with your hard-coded text
  //   navigate(
  //     `/practiceparticularcard/${subfolder_id}/${encodeURIComponent(
  //       hardCodedText
  //     )}`
  //   );
  // };
  const handleIconClick = () => {
    // Toggle dropdown visibility when icon is clicked
    setDropdownVisible(!dropdownVisible);
  };

  // Handle dropdown option selection
  const handleDropdownChange = (e) => {
    const selectedValue = e.target.value;
    setSelectedOption(selectedValue);

    const hardCodedText = "sub";

    if (!myPermissions.can_start_flashcard && selectedValue === "practiceCard" && !myPermissions.is_admin) {
      showSnackbar("You don't have permission to start Practice Flashcard.", "warning");
      return;
    }
    if (!myPermissions.can_start_quiz && selectedValue === "quizpractice" && !myPermissions.is_admin) {
      showSnackbar("You don't have permission to start Quiz Practice.", "warning");
      return;
    }

    if (selectedValue === "practiceCard") {
      navigate(`/practiceparticularcard/${subfolder_id}/${encodeURIComponent(hardCodedText)}`);
    } else if (selectedValue === "spacedrep") {
      navigate(`/practicespacedrep/${subfolder_id}/${encodeURIComponent(hardCodedText)}`);
    } else if (selectedValue === "quizpractice") {
      navigate(`/practicequiz/${subfolder_id}/${encodeURIComponent(hardCodedText)}`);
    }

    setDropdownVisible(false);
  };

  const renderContent = (html) => {
    if (!html) return null;

    // Split content into LaTeX and non-LaTeX parts
    const parts = html.split(/(\$\$.+?\$\$|\$.+?\$)/g);

    return (
      <div>
        {parts.map((part, index) => {
          // Handle block-level LaTeX ($$...$$)
          if (part.startsWith("$$") && part.endsWith("$$")) {
            const latex = part.slice(2, -2); // Remove $$ delimiters
            try {
              const renderedLatex = katex.renderToString(latex, { displayMode: true });
              return (
                <div
                  key={index}
                  dangerouslySetInnerHTML={{ __html: renderedLatex }}
                  className="rendered-content block-latex"
                />
              );
            } catch (error) {
              console.error("Block LaTeX rendering error:", error);
              return <div key={index} className="error-content">{part}</div>;
            }
          }

          // Handle inline LaTeX ($...$)
          if (part.startsWith("$") && part.endsWith("$")) {
            const latex = part.slice(1, -1); // Remove $ delimiters
            try {
              const renderedLatex = katex.renderToString(latex, { displayMode: false });
              return (
                <span
                  key={index}
                  dangerouslySetInnerHTML={{ __html: renderedLatex }}
                  className="rendered-content inline-latex"
                />
              );
            } catch (error) {
              console.error("Inline LaTeX rendering error:", error);
              return <span key={index} className="error-content">{part}</span>;
            }
          }

          // Handle non-LaTeX content
          try {
            const formattedContent = part.replace(/\n/g, "<br />"); // Replace newlines
            const allowedTags = ["b", "i", "em", "strong", "span", "br", "u", "sub", "sup", "a", "p", "img"];
            const allowedAttributes = {
              img: ["src", "alt"],
              a: ["href", "target"],
              span: ["style"],
              p: ["style"],
            };

            // Use sanitize-html or DOMPurify with selective sanitization
            const sanitizedContent = sanitizeHtml(formattedContent, {
              allowedTags,
              allowedAttributes,
              transformTags: {
                'p': 'span', // Convert <p> tags to <span>
                'div': 'span', // Convert <div> tags to <span>
              },
            });

            return (
              <span
                key={index}
                dangerouslySetInnerHTML={{ __html: sanitizedContent }}
                className="rendered-content inline-text"
              />
            );
          } catch (error) {
            console.error("HTML rendering error:", error);
            return <span key={index} className="error-content">{part}</span>;
          }
        })}
      </div>
    );
  };


  return (
    <div style={{ display: "flex" }}>
      <div className="flashcard-container">
        <div className="input-container">
          {questionType === "True/False" && <TrueFalse />}
          {questionType === "Fill in the Blanks" && <FillInTheBlanks />}
          {questionType === "Multiple Choice" && <App1 />}
          {questionType === "Image Occlusion" && <ImageOcclusion />}
          {questionType === "Single Line Choice" && (
            <div>
              <div style={{ position: "relative", marginBottom: "20px" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "20px",
                    width: "100%",
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: '800',
                      color: textColor,
                      background: mode === "dark" ? gradientLight : mode === "custom" ? "#f5f5f5" : gradientPrimary,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: 'text',
                    }}
                  >
                    Single Line Questions
                  </Typography>
                  



                  {/* Style Icon */}
                  <div style={{ position: "relative" }}>
                    {/* <StyleIcon
                      onClick={() => setDropdownVisible(!dropdownVisible)}
                      sx={{ cursor: "pointer", color:"primary.dark", '&:focus': { outline: 'none' }}}
                    /> */}


                    <Tooltip title="User Guide">
                    <IconButton
                      onClick={() => setUserGuideOpen(true)}
                      sx={{
                        color: mode === "dark" ? "#90caf9" : "#1e8bafff",
                        backgroundColor: mode === "dark" ? "rgba(144, 202, 249, 0.1)" : "rgba(30, 139, 175, 0.1)",
                        "&:hover": {
                          backgroundColor: mode === "dark" ? "rgba(144, 202, 249, 0.2)" : "rgba(30, 139, 175, 0.2)",
                        },
                        '&:focus': { outline: 'none' }
                      }}
                    >
                      <InfoIcon />
                    </IconButton>
                  </Tooltip>
                    {dropdownVisible && (
                      <div
                        style={{
                          position: "absolute",
                          top: "30px",
                          right: "-30px",
                          zIndex: 1,
                          backgroundColor:
                            mode === "dark"
                              ? "#1E1E2E"
                              : mode === "custom"
                                ? customColors.background
                                : "#e3f2fd", // light mode
                          color:
                            mode === "dark"
                              ? "#E0E0E6"
                              : mode === "custom"
                                ? customColors.text
                                : "#000000", // light mode
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                          padding: "0",
                        }}
                      >
                        {/* <div
                          onClick={() => handleDropdownChange({ target: { value: "practiceCard" } })}
                          style={{
                            cursor: myPermissions.can_start_flashcard || myPermissions.is_admin ? "pointer" : "not-allowed",
                            padding: "10px 15px",
                            transition: "background-color 0.3s ease",
                            whiteSpace: "nowrap",
                            opacity: myPermissions.can_start_flashcard || myPermissions.is_admin ? 1 : 0.5,
                          }}
                          onMouseEnter={(e) => {
                            if (myPermissions.can_start_flashcard || myPermissions.is_admin) {
                              e.currentTarget.style.backgroundColor =
                                mode === "dark"
                                  ? "#50565cff"
                                  : mode === "custom"
                                    ? customColors.background
                                    : "#bbdefb";
                            }
                          }}

                          onMouseLeave={(e) =>
                            (myPermissions.can_start_flashcard || myPermissions.is_admin) && (e.currentTarget.style.backgroundColor = "transparent")
                          }
                        >
                          Practice FlashCard
                        </div> */}
                        {/* <div
                          onClick={() => handleDropdownChange({ target: { value: "spacedrep" } })}
                          style={{
                            cursor: "pointer",
                            padding: "10px 15px",
                            transition: "background-color 0.3s ease",
                            whiteSpace: "nowrap",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.backgroundColor = darkMode ? "#50565cff" : "#bbdefb")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.backgroundColor = "transparent")
                          }
                        >
                          Practice with Spaced Repetition
                        </div> */}
                        {/* <div
                          onClick={() => handleDropdownChange({ target: { value: "quizpractice" } })}
                          style={{
                            cursor: myPermissions.can_start_quiz || myPermissions.is_admin ? "pointer" : "not-allowed",
                            padding: "10px 15px",
                            transition: "background-color 0.3s ease",
                            whiteSpace: "nowrap",
                            opacity: myPermissions.can_start_quiz || myPermissions.is_admin ? 1 : 0.5,
                          }}
                          onMouseEnter={(e) => {
                            if (myPermissions.can_start_quiz || myPermissions.is_admin) {
                              e.currentTarget.style.backgroundColor =
                                mode === "dark"
                                  ? "#50565cff"
                                  : mode === "custom"
                                    ? customColors.background
                                    : "#bbdefb";
                            }
                          }}

                          onMouseLeave={(e) =>
                            (myPermissions.can_start_quiz || myPermissions.is_admin) && (e.currentTarget.style.backgroundColor = "transparent")
                          }
                        >
                          Quiz Practice
                        </div> */}
                      </div>
                    )}
                  </div>
                </div>

                {/* Question Field with Arrow */}
                <Card
                  sx={{
                    mb: 2,
                    borderRadius: "12px",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                    backgroundColor: mode === "dark" ? "#1E1E2E" : "#fff",
                    border: "1px solid",
                    borderColor: mode === "dark" ? "#333" : "#e0e7ff",
                  }}
                >
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        cursor: "pointer",
                        mb: 1,
                      }}
                      onClick={toggleQuestion}
                    >
                      {collapsedQuestion ? <FaChevronDown /> : <FaChevronUp />}
                      <Typography variant="subtitle1" sx={{ ml: 1 }}>
                        Question <span style={{ color: "red" }}>*</span>
                      </Typography>
                    </Box>
                    <Box
                      className="editor-wrapper"
                      sx={{
                        display: collapsedQuestion ? "none" : "block",
                        position: "relative",
                        "& .ql-toolbar": {
                          borderRadius: "8px 8px 0 0 !important",
                          borderColor: mode === "dark" ? "#555 !important" : "#ccc !important",
                        },
                        "& .ql-container": {
                          borderRadius: "0 0 8px 8px !important",
                          borderColor: mode === "dark" ? "#555 !important" : "#ccc !important",
                          backgroundColor: mode === "dark" ? "#2A2A3E !important" : "#fff !important",
                          color: mode === "dark" ? "#E0E0E6 !important" : "#000 !important",
                        },
                      }}
                    >
                      <ReactQuill
                        value={currentQuestion}
                        onChange={handleQuestionChange}
                        placeholder="Enter your question"
                        modules={{
                          toolbar: [
                            ["bold", "italic", "underline", "strike"],
                            ["blockquote", "code-block"],
                            [{ header: 1 }, { header: 2 }],
                            [{ list: "ordered" }, { list: "bullet" }],
                            [{ script: "sub" }, { script: "super" }],
                            [{ indent: "-1" }, { indent: "+1" }],
                            [{ direction: "rtl" }],
                            [{ size: ["small", false, "large", "huge"] }],
                            [{ header: [1, 2, 3, 4, 5, 6, false] }],
                            [{ color: [] }, { background: [] }],
                            [{ font: [] }],
                            [{ align: [] }],
                            ["clean"],
                          ],
                          textLimit: { limit: CHAR_LIMIT },
                        }}
                        formats={[
                          "header", "font", "size", "bold", "italic", "underline", "strike",
                          "blockquote", "code-block", "list", "bullet", "indent", "link",
                          "image", "video", "color", "background", "align", "script",
                          { name: "preserve-spaces", type: "inline" },
                        ]}
                        style={{
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "pre-wrap",
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          position: "absolute",
                          bottom: "10px",
                          right: "10px",
                          color: mode === "dark" ? "#aaa" : "#666",
                        }}
                      >
                        {questionCharCount}/{CHAR_LIMIT}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>

                {/* Answer Field with Arrow */}
                <Card
                  sx={{
                    mb: 2,
                    borderRadius: "12px",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                    backgroundColor: mode === "dark" ? "#1E1E2E" : "#fff",
                    border: "1px solid",
                    borderColor: mode === "dark" ? "#333" : "#e0e7ff",
                  }}
                >
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        cursor: "pointer",
                        mb: 1,
                      }}
                      onClick={toggleAnswer}
                    >
                      {collapsedAnswer ? <FaChevronDown /> : <FaChevronUp />}
                      <Typography variant="subtitle1" sx={{ ml: 1 }}>
                        Answer <span style={{ color: "red" }}>*</span>
                      </Typography>
                    </Box>
                    <Box
                      className="editor-wrapper"
                      sx={{
                        display: collapsedAnswer ? "none" : "block",
                        position: "relative",
                        "& .ql-toolbar": {
                          borderRadius: "8px 8px 0 0 !important",
                          borderColor: mode === "dark" ? "#555 !important" : "#ccc !important",
                        },
                        "& .ql-container": {
                          borderRadius: "0 0 8px 8px !important",
                          borderColor: mode === "dark" ? "#555 !important" : "#ccc !important",
                          backgroundColor: mode === "dark" ? "#2A2A3E !important" : "#fff !important",
                          color: mode === "dark" ? "#E0E0E6 !important" : "#000 !important",
                        },
                      }}
                    >
                      <ReactQuill
                        value={currentAnswer}
                        onChange={handleAnswerChange}
                        placeholder="Enter your answer"
                        modules={{
                          toolbar: [
                            ["bold", "italic", "underline", "strike"],
                            ["blockquote", "code-block"],
                            [{ header: 1 }, { header: 2 }],
                            [{ list: "ordered" }, { list: "bullet" }],
                            [{ script: "sub" }, { script: "super" }],
                            [{ indent: "-1" }, { indent: "+1" }],
                            [{ direction: "rtl" }],
                            [{ size: ["small", false, "large", "huge"] }],
                            [{ header: [1, 2, 3, 4, 5, 6, false] }],
                            [{ color: [] }, { background: [] }],
                            [{ font: [] }],
                            [{ align: [] }],
                            ["clean"],
                          ],
                          textLimit: { limit: CHAR_LIMIT },
                        }}
                        formats={[
                          "header", "font", "size", "bold", "italic", "underline", "strike",
                          "blockquote", "code-block", "list", "bullet", "indent", "link",
                          "image", "video", "color", "background", "align", "script",
                          { name: "preserve-spaces", type: "inline" },
                        ]}
                        style={{
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "pre-wrap",
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          position: "absolute",
                          bottom: "10px",
                          right: "10px",
                          color: mode === "dark" ? "#aaa" : "#666",
                        }}
                      >
                        {answerCharCount}/{CHAR_LIMIT}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>

                {/* Explanation Field with Arrow */}
                <Card
                  sx={{
                    mb: 2,
                    borderRadius: "12px",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                    backgroundColor: mode === "dark" ? "#1E1E2E" : "#fff",
                    border: "1px solid",
                    borderColor: mode === "dark" ? "#333" : "#e0e7ff",
                  }}
                >
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        cursor: "pointer",
                        mb: 1,
                      }}
                      onClick={toggleExplanation}
                    >
                      {collapsedExplanation ? <FaChevronDown /> : <FaChevronUp />}
                      <Typography variant="subtitle1" sx={{ ml: 1 }}>
                        Explanation
                      </Typography>
                    </Box>
                    <Box
                      className="editor-wrapper"
                      sx={{
                        display: collapsedExplanation ? "none" : "block",
                        position: "relative",
                        "& .ql-toolbar": {
                          borderRadius: "8px 8px 0 0 !important",
                          borderColor: mode === "dark" ? "#555 !important" : "#ccc !important",
                        },
                        "& .ql-container": {
                          borderRadius: "0 0 8px 8px !important",
                          borderColor: mode === "dark" ? "#555 !important" : "#ccc !important",
                          backgroundColor: mode === "dark" ? "#2A2A3E !important" : "#fff !important",
                          color: mode === "dark" ? "#E0E0E6 !important" : "#000 !important",
                        },
                      }}
                    >
                      <ReactQuill
                        value={currentExplanation}
                        onChange={handleExplanationChange}
                        placeholder="Enter your explanation"
                        modules={{
                          toolbar: [
                            ["bold", "italic", "underline", "strike"],
                            ["blockquote", "code-block"],
                            [{ header: 1 }, { header: 2 }],
                            [{ list: "ordered" }, { list: "bullet" }],
                            [{ script: "sub" }, { script: "super" }],
                            [{ indent: "-1" }, { indent: "+1" }],
                            [{ direction: "rtl" }],
                            [{ size: ["small", false, "large", "huge"] }],
                            [{ header: [1, 2, 3, 4, 5, 6, false] }],
                            [{ color: [] }, { background: [] }],
                            [{ font: [] }],
                            [{ align: [] }],
                            ["clean"],
                          ],
                          textLimit: { limit: CHAR_LIMIT },
                        }}
                        formats={[
                          "header", "font", "size", "bold", "italic", "underline", "strike",
                          "blockquote", "code-block", "list", "bullet", "indent", "link",
                          "image", "video", "color", "background", "align", "script",
                          { name: "preserve-spaces", type: "inline" },
                        ]}
                        style={{
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "pre-wrap",
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          position: "absolute",
                          bottom: "10px",
                          right: "10px",
                          color: mode === "dark" ? "#aaa" : "#666",
                        }}
                      >
                        {explanationCharCount}/{CHAR_LIMIT}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </div>
              <div
                onClick={() => {
                  if (!myPermissions.can_create_sub && !myPermissions.is_admin) {
                    showSnackbar("You don't have permission to create a SUB flashcard.", "warning");
                    return;
                  }
                  handleAddFlashcard();
                }}
                style={{ display: "inline-block", cursor: (myPermissions.can_create_sub || myPermissions.is_admin) ? "pointer" : "not-allowed" }}
              >
                <Button
                  variant="contained"
                  disabled={!(myPermissions.can_create_sub || myPermissions.is_admin)}
                  size="small"   // <-- reduces height automatically
                  sx={{
                    background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                    color: "white",
                    // padding: "14px 40px",
                    // fontSize: "1.1rem",
                    padding: "6px 16px",     // reduced size
                    fontSize: "0.85rem",     // smaller text
                    fontWeight: "700",
                    // borderRadius: "12px",
                    borderRadius: "8px",     // slightly smaller radius
                    textTransform: "none",

                    boxShadow: "0 8px 25px rgba(30, 58, 138, 0.4)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      background: "linear-gradient(135deg, #1e8bafff 0%, #1e3a8a 100%)",
                      transform: "translateY(-3px)",
                      boxShadow: "0 12px 35px rgba(30, 58, 138, 0.5)",
                    },
                    "&.Mui-disabled": {
                      background: "#d1d5db",
                      color: "white",
                      opacity: 0.6,
                    },
                  }}
                  startIcon={<AddIcon></AddIcon>}
                >
                  ADD FLASHCARD
                </Button>
              </div>

              {myPermissions.can_read_sub || myPermissions.is_admin ? (
                <div>
                  <ul className="card-list">
                    {cards.map((card, index) => (
                      <Card
                        key={card.id}
                        sx={{
                          mt: 3,
                          mb: 2,
                          borderRadius: '16px',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            boxShadow: '0 8px 30px rgba(102, 126, 234, 0.2)',
                            transform: 'translateY(-4px)',
                          },
                          backgroundColor: mode === 'dark' ? '#2A2A3E' : '#fff',
                          border: '1px solid',
                          borderColor: mode === 'dark' ? '#333' : '#e0e7ff',
                          position: 'relative',
                          padding: '16px',
                        }}
                      >
                        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              wordBreak: "break-word",
                              overflowWrap: "break-word",
                              paddingRight: "40px",
                              maxWidth: "calc(100% - 70px)",
                              whiteSpace: "pre-wrap",
                              marginBottom: "16px",
                            }}
                          >
                            <div
                              style={{
                                background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                                color: "white",
                                padding: "8px 14px",
                                borderRadius: "10px",
                                fontWeight: "800",
                                fontSize: "1.1rem",
                                marginRight: "16px",
                                minWidth: "45px",
                                textAlign: "center",
                                boxShadow: "0 4px 15px rgba(30, 58, 138, 0.3)",
                              }}
                            >
                              Q
                            </div>

                            <span style={{ flex: 1, display: "flex", alignItems: "center", }}>
                              <strong>{renderContent(card.question)}</strong>
                            </span>
                          </div>
                          <div
                            style={{
                              position: "absolute",
                              top: "20px",
                              right: "20px",
                              display: "flex",
                              gap: "14px",
                              zIndex: 10,
                              // background: "white", // or use a dark background if in dark mode: `mode === 'dark' ? '#2A2A3E' : '#fff'`
                              padding: "10px 14px",
                              borderRadius: "12px",
                              // boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
                              backdropFilter: "blur(10px)",
                            }}
                          >
                            {/* Expand/Collapse Icon */}
                            <i
                              className={`bi ${card.expanded ? "bi-arrows-angle-contract" : "bi-arrows-angle-expand"}`}
                              title={card.expanded ? "Contract" : "Expand"}
                              onClick={() => toggleCardExpand(index)}
                              style={{
                                cursor: "pointer",
                                fontSize: "1.2rem",
                                color: "#667eea",
                                transition: "all 0.2s ease",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.color = "#764ba2";
                                e.currentTarget.style.transform = "scale(1.2)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.color = "#667eea";
                                e.currentTarget.style.transform = "scale(1)";
                              }}
                            ></i>

                            {/* Edit Icon */}
                            <i
                              className="bi bi-pencil-square"
                              title="Edit"
                              onClick={() => openEditModal(index)}
                              style={{
                                cursor: (myPermissions.can_update_sub || myPermissions.is_admin) ? "pointer" : "not-allowed",
                                opacity: (myPermissions.can_update_sub || myPermissions.is_admin) ? 1 : 0.4,
                                fontSize: "1.2rem",
                                color: "#10b981",
                                transition: "all 0.2s ease",
                              }}
                              onMouseEnter={(e) => {
                                if (myPermissions.can_update_sub || myPermissions.is_admin) {
                                  e.currentTarget.style.color = "#059669";
                                  e.currentTarget.style.transform = "scale(1.2)";
                                }
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.color = "#10b981";
                                e.currentTarget.style.transform = "scale(1)";
                              }}
                            ></i>

                            {/* Delete Icon */}
                            <i
                              className="bi bi-trash3"
                              title="Delete"
                              onClick={() => openDeleteDialog(card.id)}
                              style={{
                                cursor: (myPermissions.can_delete_sub || myPermissions.is_admin) ? "pointer" : "not-allowed",
                                opacity: (myPermissions.can_delete_sub || myPermissions.is_admin) ? 1 : 0.4,
                                fontSize: "1.2rem",
                                color: "#ef4444",
                                transition: "all 0.2s ease",
                              }}
                              onMouseEnter={(e) => {
                                if (myPermissions.can_delete_sub || myPermissions.is_admin) {
                                  e.currentTarget.style.color = "#dc2626";
                                  e.currentTarget.style.transform = "scale(1.2)";
                                }
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.color = "#ef4444";
                                e.currentTarget.style.transform = "scale(1)";
                              }}
                            ></i>

                            {/* Tag Icon */}
                            <i
                              className="bi bi-tags"
                              title="Tag"
                              onClick={(e) => openTagModal(index, e)}
                              style={{
                                // cursor: (myPermissions.can_tag_sub || myPermissions.is_admin) ? "pointer" : "not-allowed",
                                cursor: "pointer",
                                // opacity: (myPermissions.can_tag_sub || myPermissions.is_admin) ? 1 : 0.4,
                                opacity: 1,
                                fontSize: "1.2rem",
                                color: "#8b5cf6",
                                transition: "all 0.2s ease",
                              }}
                              onMouseEnter={(e) => {
                                if (myPermissions.can_tag_sub || myPermissions.is_admin) {
                                  e.currentTarget.style.color = "#7c3aed";
                                  e.currentTarget.style.transform = "scale(1.2)";
                                }
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.color = "#8b5cf6";
                                e.currentTarget.style.transform = "scale(1)";
                              }}
                            ></i>
                          </div>

                          {(card.expanded && (myPermissions.can_read_sub || myPermissions.is_admin)) && (
                            <>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  wordBreak: "break-word",
                                  overflowWrap: "break-word",
                                  whiteSpace: "pre-wrap",
                                  marginBottom: "16px",
                                }}
                              >
                                <div
                                  style={{
                                    background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                                    color: "white",
                                    padding: "8px 14px",
                                    borderRadius: "10px",
                                    fontWeight: "800",
                                    fontSize: "1.1rem",
                                    marginRight: "16px",
                                    minWidth: "45px",
                                    textAlign: "center",
                                    boxShadow: "0 4px 15px rgba(16, 185, 129, 0.3)",
                                  }}
                                >
                                  A
                                </div>

                                <span style={{ flex: 1, display: "flex", alignItems: "center", }}>{renderContent(card.answer)}</span>
                              </div>
                              {card.explanation && (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    wordBreak: "break-word",
                                    overflowWrap: "break-word",
                                    whiteSpace: "pre-wrap",
                                    marginBottom: "16px",
                                  }}
                                >
                                  <div
                                    style={{
                                      background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                                      color: "white",
                                      padding: "6px 12px",
                                      borderRadius: "8px",
                                      fontWeight: "700",
                                      fontSize: "0.9rem",
                                      marginRight: "14px",
                                      minWidth: "fit-content",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    💡 Explanation
                                  </div>

                                  <span style={{ flex: 1, display: "flex", alignItems: "center", }}>{renderContent(card.explanation)}</span>
                                </div>
                              )}
                              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "8px" }}>

                                {card.tags.map((tag, index) => (
                                  <Chip key={index} label={`#${tag.name}`} size="small" />
                                ))}

                              </div>
                            </>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </ul>
                </div>
              ) : (
                <div style={{ textAlign: "center", marginTop: "20px" }}>
                  You don't have permission to view SUB flashcards.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <div
        className={`vertical-toolbar ${collapsed ? "collapsed" : ""}`}
        style={{
          background: mode === "dark"
            ? "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)"
            : mode === "custom"
              ? customColors.bgProfile
              : "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
          position: "sticky", // Make the toolbar sticky
          top: "100px", // Adjust this value to position the toolbar where you want it to stick
          left: 0, // Align to the left
          // height: "fit-content", // Let the height adjust to content
          zIndex: 1000, // Ensure it stays above other content
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "8px 0", // Add some padding for better appearance
        }}
      >
        {/* <i
          className="bi bi-card-list"
          title="Single Line Choice Card"
          onClick={() => handleToolbarClick("Single Line Choice")}
        ></i> */}
        <i
          className="bi bi-pencil"
          title="Fill in the Blanks"
          onClick={() => handleToolbarClick("Fill in the Blanks")}
        ></i>
        <i
          className="bi bi-toggle-on"
          title="True/False"
          onClick={() => handleToolbarClick("True/False")}
        ></i>
        <i
          className="bi bi-ui-radios"
          title="Multiple Choice Q&A"
          onClick={() => handleToolbarClick("Multiple Choice")}
        ></i>
        {/* <i
          className="bi bi-textarea-resize"
          title="Multiple Line Choice Card"
          onClick={() => handleToolbarClick("Multiple Line Choice")}
        ></i> */}
        <i
          className="bi bi-image"
          title="Image Occlusion"
          onClick={() => handleToolbarClick("Image Occlusion")}
        ></i>
        {/* <i className="bi bi-trash" title="Delete"></i>
        <i className="bi bi-type-h1" title="Heading"></i>
        <i className="bi bi-plus" title="Add"></i> */}
        <ExpandIcon
          titleAccess="Collapse"
          onClick={toggleCollapse}
          sx={{
            cursor: 'pointer',
            fontSize: '34px', // Adjust size to match other icons
            marginTop: 'auto',
            '&:hover': {
              opacity: 0.7
            }
          }}
        />
      </div>
    <Dialog 
  open={editModalOpen} 
  onClose={handleCloseModal}
  maxWidth="md"
  fullWidth
  PaperProps={{
    sx: {
      borderRadius: '16px',
      background: mode === 'dark' ? '#1E1E2E' : mode === 'custom' ? customColors.cardBg : '#ffffff',
      boxShadow: mode === 'dark' 
        ? '0 8px 32px rgba(0, 0, 0, 0.4)' 
        : '0 8px 32px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden',
    }
  }}
>
  {/* Gradient Header */}
  <Box sx={{
    background: mode === 'dark' 
      ? 'linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)'
      : mode === 'custom' 
        ? customColors.gradient 
        : 'linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)',
    p: 3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    color: 'white',
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box sx={{
        width: 48,
        height: 48,
        borderRadius: '12px',
        background: 'rgba(255, 255, 255, 0.2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <i className="bi bi-pencil-square" style={{ fontSize: 28 }}></i>
      </Box>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: '700', mb: 0.5 }}>
          Edit Flashcard
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9 }}>
          Update your flashcard details below
        </Typography>
      </Box>
    </Box>
  </Box>

  {/* Content with improved styling */}
  <DialogContent sx={{ 
    p: 4,
    background: mode === 'dark' ? '#1E1E2E' : mode === 'custom' ? customColors.background : '#ffffff',
   "&::-webkit-scrollbar": {
      width: "10px",
    },
    "&::-webkit-scrollbar-track": {
      background: mode === "dark" ? "#2d3748" : "#f1f5f9",
      borderRadius: "6px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: mode === "dark" ? "#475569" : "#94a3b8",
      borderRadius: "6px",
      border: `2px solid ${mode === "dark" ? "#2d3748" : "#f1f5f9"}`,
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: "#64748b",
    },
    scrollbarWidth: "thin",
    scrollbarColor: mode === "dark"
      ? "#475569 #2d3748"
      : "#94a3b8 #f1f5f9",
  }}
>
    {/* Question Field */}
    <Box sx={{ mb: 3 }}>
      <Typography 
        variant="subtitle1" 
        sx={{ 
          mb: 1.5, 
          fontWeight: 600,
          color: textColor,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)',
        }} />
        Question <span style={{ color: '#ef4444' }}>*</span>
      </Typography>
      <Box className={`editor-wrapper ${mode}-mode`} sx={{ position: 'relative' }}>
        <ReactQuill
          value={editQuestion}
          onChange={handleEditQuestionChange}
          modules={{
            toolbar: [
              ['bold', 'italic', 'underline', 'strike'],
              ['blockquote', 'code-block'],
              [{ header: 1 }, { header: 2 }],
              [{ list: 'ordered' }, { list: 'bullet' }],
              [{ script: 'sub' }, { script: 'super' }],
              [{ indent: '-1' }, { indent: '+1' }],
              [{ size: ['small', false, 'large', 'huge'] }],
              [{ color: [] }, { background: [] }],
              [{ align: [] }],
              ['clean'],
            ],
            textLimit: { limit: CHAR_LIMIT },
          }}
        />
        <Typography
          variant="caption"
          sx={{
            position: 'absolute',
            bottom: '10px',
            right: '10px',
            color: mode === 'dark' ? '#aaa' : '#666',
            background: mode === 'dark' ? '#2A2A3E' : '#fff',
            px: 1,
            py: 0.5,
            borderRadius: '4px',
            fontWeight: 500,
          }}
        >
          {editQuestionCharCount}/{CHAR_LIMIT}
        </Typography>
      </Box>
    </Box>

    {/* Answer Field */}
    <Box sx={{ mb: 3 }}>
      <Typography 
        variant="subtitle1" 
        sx={{ 
          mb: 1.5, 
          fontWeight: 600,
          color: textColor,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        }} />
        Answer <span style={{ color: '#ef4444' }}>*</span>
      </Typography>
      <Box className={`editor-wrapper ${mode}-mode`} sx={{ position: 'relative' }}>
        <ReactQuill
          value={editAnswers}
          onChange={handleEditAnswerChange}
          modules={{
            toolbar: [
              ['bold', 'italic', 'underline', 'strike'],
              ['blockquote', 'code-block'],
              [{ header: 1 }, { header: 2 }],
              [{ list: 'ordered' }, { list: 'bullet' }],
              [{ script: 'sub' }, { script: 'super' }],
              [{ indent: '-1' }, { indent: '+1' }],
              [{ size: ['small', false, 'large', 'huge'] }],
              [{ color: [] }, { background: [] }],
              [{ align: [] }],
              ['clean'],
            ],
            textLimit: { limit: CHAR_LIMIT },
          }}
        />
        <Typography
          variant="caption"
          sx={{
            position: 'absolute',
            bottom: '10px',
            right: '10px',
            color: mode === 'dark' ? '#aaa' : '#666',
            background: mode === 'dark' ? '#2A2A3E' : '#fff',
            px: 1,
            py: 0.5,
            borderRadius: '4px',
            fontWeight: 500,
          }}
        >
          {editAnswerCharCount}/{CHAR_LIMIT}
        </Typography>
      </Box>
    </Box>

    {/* Explanation Field */}
    <Box>
      <Typography 
        variant="subtitle1" 
        sx={{ 
          mb: 1.5, 
          fontWeight: 600,
          color: textColor,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        }} />
        Explanation
      </Typography>
      <Box className={`editor-wrapper ${mode}-mode`} sx={{ position: 'relative' }}>
        <ReactQuill
          value={editExplanation}
          onChange={handleEditExplanationChange}
          modules={{
            toolbar: [
              ['bold', 'italic', 'underline', 'strike'],
              ['blockquote', 'code-block'],
              [{ header: 1 }, { header: 2 }],
              [{ list: 'ordered' }, { list: 'bullet' }],
              [{ script: 'sub' }, { script: 'super' }],
              [{ indent: '-1' }, { indent: '+1' }],
              [{ size: ['small', false, 'large', 'huge'] }],
              [{ color: [] }, { background: [] }],
              [{ align: [] }],
              ['clean'],
            ],
            textLimit: { limit: CHAR_LIMIT },
          }}
        />
        <Typography
          variant="caption"
          sx={{
            position: 'absolute',
            bottom: '10px',
            right: '10px',
            color: mode === 'dark' ? '#aaa' : '#666',
            background: mode === 'dark' ? '#2A2A3E' : '#fff',
            px: 1,
            py: 0.5,
            borderRadius: '4px',
            fontWeight: 500,
          }}
        >
          {editExplanationCharCount}/{CHAR_LIMIT}
        </Typography>
      </Box>
    </Box>
  </DialogContent>

  <Divider sx={{ 
    borderColor: mode === 'dark' ? '#333' : mode === 'custom' ? customColors.border : '#e5e7eb' 
  }} />

  {/* Enhanced Actions */}
  <DialogActions sx={{ 
    p: 3, 
    gap: 2,
    background: mode === 'dark' ? '#2A2A3E' : mode === 'custom' ? customColors.background : '#f8f9fa',
  }}>
    <Button 
      onClick={handleCloseModal}
      variant="outlined"
      sx={{ 
        borderColor: mode === 'dark' ? '#555' : mode === 'custom' ? customColors.border : '#d1d5db',
        color: mode === 'dark' ? '#A0A0A6' : mode === 'custom' ? customColors.textSecondary : '#6B7280',
        '&:hover': {
          borderColor: textColor,
          background: 'transparent',
        }
      }}
    >
      Cancel
    </Button>
    <Button 
      onClick={handleUpdateCard}
      variant="contained"
      disabled={!(myPermissions.can_update_sub || myPermissions.is_admin)}
      sx={{ 
        background: 'linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)',
        color: 'white',
        fontWeight: 600,
        px: 4,
        '&:hover': {
          background: 'linear-gradient(135deg, #1e8bafff 0%, #1e3a8a 100%)',
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 20px rgba(30, 58, 138, 0.3)',
        },
        '&.Mui-disabled': {
          background: '#d1d5db',
          color: 'white',
          opacity: 0.6,
        },
        transition: 'all 0.3s ease',
      }}
    >
      Save Changes
    </Button>
  </DialogActions>
</Dialog>

   <Dialog 
  open={deleteDialogOpen} 
  onClose={handleCloseDeleteDialog}
  maxWidth="sm"
  fullWidth
  PaperProps={{
    sx: {
      borderRadius: '20px',
      background: mode === 'dark' ? '#1E1E2E' : mode === 'custom' ? customColors.cardBg : '#ffffff',
      boxShadow: mode === 'dark' 
        ? '0 8px 32px rgba(0, 0, 0, 0.4)' 
        : '0 8px 32px rgba(239, 68, 68, 0.2)',
      overflow: 'hidden',
    }
  }}
>
  {/* Warning Header */}
  <Box sx={{
    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    p: 3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    color: 'white',
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box sx={{
        width: 48,
        height: 48,
        borderRadius: '12px',
        background: 'rgba(255, 255, 255, 0.2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        {/* <i className="bi bi-exclamation-triangle" style={{ fontSize: 28 }}></i> */}
        <i className="bi bi-trash3" style={{ 
          fontSize: 40, 
          // color: '#ef4444',
        }}></i>
      </Box>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: '700', mb: 0.5 }}>
          Delete Flashcard
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9 }}>
          This action cannot be undone
        </Typography>
      </Box>
    </Box>
    {/* <IconButton 
      onClick={handleCloseDeleteDialog}
      sx={{ 
        color: 'white',
        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
      }}
    >
      <CloseIcon />
    </IconButton> */}
  </Box>

  {/* Content */}
  <DialogContent sx={{ 
    p: 4,
    background: mode === 'dark' ? '#1E1E2E' : mode === 'custom' ? customColors.background : '#ffffff',
  }}>
    <Box sx={{
      textAlign: 'center',
      py: 2,
    }}>
      {/* <Box sx={{
        width: 80,
        height: 80,
        borderRadius: '50%',
        background: mode === 'dark' 
          ? 'rgba(239, 68, 68, 0.1)' 
          : 'rgba(239, 68, 68, 0.05)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 24px',
      }}>
        
      </Box> */}
      <Typography 
        variant="h6" 
        sx={{ 
          mb: 2, 
          color: textColor,
          fontWeight: 600,
        }}
      >
        Are you sure you want to delete this flashcard?
      </Typography>
      <Typography 
        variant="body2" 
        sx={{ 
          color: mode === 'dark' ? '#A0A0A6' : mode === 'custom' ? customColors.textSecondary : '#6B7280',
          lineHeight: 1.6,
        }}
      >
        This will permanently remove the flashcard and all associated data. This action cannot be reversed.
      </Typography>
    </Box>
  </DialogContent>

  <Divider sx={{ 
    borderColor: mode === 'dark' ? '#333' : mode === 'custom' ? customColors.border : '#e5e7eb' 
  }} />

  {/* Actions */}
  <DialogActions sx={{ 
    p: 3, 
    gap: 2,
    background: mode === 'dark' ? '#2A2A3E' : mode === 'custom' ? customColors.background : '#f8f9fa',
  }}>
    <Button 
      onClick={handleCloseDeleteDialog}
      variant="outlined"
      fullWidth
      sx={{ 
        borderColor: mode === 'dark' ? '#555' : mode === 'custom' ? customColors.border : '#d1d5db',
        color: textColor,
        fontWeight: 600,
        '&:hover': {
          borderColor: textColor,
          background: 'transparent',
        }
      }}
    >
      Cancel
    </Button>
    <Button 
      onClick={handleDeleteCard}
      variant="contained"
      fullWidth
      disabled={!(myPermissions.can_delete_sub || myPermissions.is_admin)}
      sx={{ 
        background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
        color: 'white',
        fontWeight: 600,
        '&:hover': {
          background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 20px rgba(239, 68, 68, 0.4)',
        },
        '&.Mui-disabled': {
          background: '#d1d5db',
          color: 'white',
          opacity: 0.6,
        },
        transition: 'all 0.3s ease',
      }}
    >
      Delete Flashcard
    </Button>
  </DialogActions>
</Dialog>
      <Dialog open={duplicateDialogOpen} onClose={handleCancelDuplicate}>
        <DialogTitle sx={{ background: mode === 'dark' ? 'linear-gradient(to right, #142be1, #44a6e9)' : mode === 'custom' ? customColors.background : 'linear-gradient(to right, #142be1, #44a6e9)' }}>Duplicate Question</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: mode === "dark" ? '#fff' : mode === "custom" ? customColors.text : '#666' }}>
            A flashcard with this question already exists. Do you want to add it anyway?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDuplicate} sx={{
            backgroundColor: mode === 'dark' ? '#142be1' : mode === 'custom' ? customColors.primary : '#44a6e9',
            '&:hover': { backgroundColor: mode === 'dark' ? '#0d1abf' : mode === 'custom' ? customColors.background : '#0d1abf' }
          }}>Cancel</Button>
          <Button
            sx={{
              backgroundColor: mode === 'dark' ? '#142be1' : mode === 'custom' ? customColors.primary : '#44a6e9',
              '&:hover': { backgroundColor: mode === 'dark' ? '#0d1abf' : mode === 'custom' ? customColors.background : '#0d1abf' }
            }}
            onClick={handleConfirmDuplicate}
            disabled={!(myPermissions.can_create_sub || myPermissions.is_admin)}
          >Add Anyway</Button>
        </DialogActions>
      </Dialog>
    <Dialog
  open={userGuideOpen}
  onClose={() => setUserGuideOpen(false)}
  maxWidth="md"
  fullWidth
  PaperProps={{
    sx: {
      borderRadius: "16px",
      boxShadow: mode === "dark"
        ? "0 8px 32px rgba(0, 0, 0, 0.4)"
        : "0 8px 32px rgba(30, 139, 175, 0.15)",
      overflow: "hidden",
      background: mode === "dark"
        ? "#1E1B18"
        : mode === "custom"
          ? customColors.bg
          : "#ffffff",
    },
  }}
>
  {/* Header */}
  <Box
    sx={{
      background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
      p: 3,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      color: "white",
    }}
  >
    <Box>
      <Typography variant="h5" sx={{ fontWeight: "700", mb: 0.5 }}>
        User Guide
      </Typography>
      <Typography variant="body2" sx={{ opacity: 0.9 }}>
        Single Line Questions
      </Typography>
    </Box>
    <IconButton
      onClick={() => setUserGuideOpen(false)}
      sx={{
        color: "white",
        "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
      }}
    >
      <CloseIcon />
    </IconButton>
  </Box>

  {/* Content */}
  <DialogContent
    sx={{
      p: 4,
      overflowY: "auto",
      maxHeight: "75vh",
      "&::-webkit-scrollbar": {
        width: "10px",
      },
      "&::-webkit-scrollbar-track": {
        background: mode === "dark"
          ? "#2d3748"
          : mode === "custom"
            ? customColors.scrollTrack
            : "#f1f5f9",
        borderRadius: "6px",
      },
      "&::-webkit-scrollbar-thumb": {
        background: mode === "dark"
          ? "#475569"
          : mode === "custom"
            ? customColors.scrollThumb
            : "#94a3b8",
        borderRadius: "6px",
        border: `2px solid ${
          mode === "dark"
            ? "#2d3748"
            : mode === "custom"
              ? customColors.border
              : "#f1f5f9"
        }`,
      },
      "&::-webkit-scrollbar-thumb:hover": {
        background: mode === "dark"
          ? "#64748b"
          : mode === "custom"
            ? customColors.scrollThumbHover
            : "#475569",
      },
      scrollbarWidth: "thin",
      scrollbarColor: mode === "dark"
        ? "#475569 #2d3748"
        : mode === "custom"
          ? `${customColors.scrollThumb} ${customColors.scrollTrack}`
          : "#94a3b8 #f1f5f9",
      background: mode === "dark"
        ? "#1E1B18"
        : mode === "custom"
          ? customColors.bg
          : "#ffffff",
      color: mode === "dark"
        ? "rgba(255, 255, 255, 0.87)"
        : mode === "custom"
          ? customColors.text
          : "rgba(30, 27, 24, 0.87)",
    }}
  >
    {/* Question Section */}
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mr: 2,
          }}
        >
          <i className="bi bi-card-text" style={{ color: "white", fontSize: 20 }}></i>
        </Box>
        <Typography
          variant="h6"
          sx={{
            fontWeight: "600",
            color: mode === "dark"
              ? "#e0e0e0"
              : mode === "custom"
                ? customColors.text
                : "#1E1B18",
          }}
        >
          Question
        </Typography>
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: mode === "dark"
            ? "rgba(255, 255, 255, 0.7)"
            : mode === "custom"
              ? customColors.textSecondary
              : "rgba(30, 27, 24, 0.7)",
          lineHeight: 1.7,
          pl: 7,
        }}
      >
        Enter your question in the <strong>Question</strong> field. You can use rich text formatting, including bold, italics, and LaTeX for mathematical expressions.
      </Typography>
    </Box>

    {/* Answer Section */}
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mr: 2,
          }}
        >
          <i className="bi bi-chat-text" style={{ color: "white", fontSize: 20 }}></i>
        </Box>
        <Typography
          variant="h6"
          sx={{
            fontWeight: "600",
            color: mode === "dark"
              ? "#e0e0e0"
              : mode === "custom"
                ? customColors.text
                : "#1E1B18",
          }}
        >
          Answer
        </Typography>
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: mode === "dark"
            ? "rgba(255, 255, 255, 0.7)"
            : mode === "custom"
              ? customColors.textSecondary
              : "rgba(30, 27, 24, 0.7)",
          lineHeight: 1.7,
          pl: 7,
        }}
      >
        Enter the answer in the <strong>Answer</strong> field. You can also use rich text formatting here.
      </Typography>
    </Box>

    {/* Explanation Section */}
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mr: 2,
          }}
        >
          <i className="bi bi-lightbulb" style={{ color: "white", fontSize: 20 }}></i>
        </Box>
        <Typography
          variant="h6"
          sx={{
            fontWeight: "600",
            color: mode === "dark"
              ? "#e0e0e0"
              : mode === "custom"
                ? customColors.text
                : "#1E1B18",
          }}
        >
          Explanation
        </Typography>
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: mode === "dark"
            ? "rgba(255, 255, 255, 0.7)"
            : mode === "custom"
              ? customColors.textSecondary
              : "rgba(30, 27, 24, 0.7)",
          lineHeight: 1.7,
          pl: 7,
        }}
      >
        Optionally, add an explanation in the <strong>Explanation</strong> field. This can provide additional context or details about the answer.
      </Typography>
    </Box>

    {/* Add Flashcard Section */}
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mr: 2,
          }}
        >
          <AddIcon sx={{ color: "white", fontSize: 20 }} />
        </Box>
        <Typography
          variant="h6"
          sx={{
            fontWeight: "600",
            color: mode === "dark"
              ? "#e0e0e0"
              : mode === "custom"
                ? customColors.text
                : "#1E1B18",
          }}
        >
          Add Flashcard
        </Typography>
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: mode === "dark"
            ? "rgba(255, 255, 255, 0.7)"
            : mode === "custom"
              ? customColors.textSecondary
              : "rgba(30, 27, 24, 0.7)",
          lineHeight: 1.7,
          pl: 7,
        }}
      >
        Click the <strong>Add Flashcard</strong> button to save your question, answer, and explanation. If the question already exists, you'll be prompted to confirm.
      </Typography>
    </Box>

    {/* Flashcard List Section */}
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mr: 2,
          }}
        >
          <i className="bi bi-card-list" style={{ color: "white", fontSize: 20 }}></i>
        </Box>
        <Typography
          variant="h6"
          sx={{
            fontWeight: "600",
            color: mode === "dark"
              ? "#e0e0e0"
              : mode === "custom"
                ? customColors.text
                : "#1E1B18",
          }}
        >
          Flashcard List
        </Typography>
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: mode === "dark"
            ? "rgba(255, 255, 255, 0.7)"
            : mode === "custom"
              ? customColors.textSecondary
              : "rgba(30, 27, 24, 0.7)",
          lineHeight: 1.7,
          pl: 7,
        }}
      >
        View your list of flashcards below. You can <strong>expand</strong>, <strong>edit</strong>, <strong>delete</strong>, or <strong>tag</strong> each flashcard.
      </Typography>
    </Box>

    {/* Pro Tips */}
    <Box
      sx={{
        p: 3,
        borderRadius: "12px",
        bgcolor: mode === "dark"
          ? "rgba(144, 202, 249, 0.05)"
          : mode === "custom"
            ? customColors.bgSecondary
            : "rgba(30, 139, 175, 0.05)",
        border: mode === "dark"
          ? "1px solid rgba(144, 202, 249, 0.2)"
          : mode === "custom"
            ? `1px solid ${customColors.border}`
            : "1px solid rgba(30, 139, 175, 0.2)",
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: "600",
          color: mode === "dark"
            ? "#90caf9"
            : mode === "custom"
              ? customColors.text
              : "#1e8bafff",
          mb: 2,
        }}
      >
        💡 Quick Tips
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography
          variant="body2"
          sx={{
            color: mode === "dark"
              ? "rgba(255, 255, 255, 0.7)"
              : mode === "custom"
                ? customColors.textSecondary
                : "rgba(30, 27, 24, 0.7)",
            lineHeight: 1.7,
          }}
        >
          • Use the toolbar to switch between different flashcard types.
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: mode === "dark"
              ? "rgba(255, 255, 255, 0.7)"
              : mode === "custom"
                ? customColors.textSecondary
                : "rgba(30, 27, 24, 0.7)",
            lineHeight: 1.7,
          }}
        >
          • Use LaTeX for mathematical expressions by enclosing them in `$...$` or `$$...$$`.
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: mode === "dark"
              ? "rgba(255, 255, 255, 0.7)"
              : mode === "custom"
                ? customColors.textSecondary
                : "rgba(30, 27, 24, 0.7)",
            lineHeight: 1.7,
          }}
        >
          • Click the <strong>Expand/Collapse</strong> icon to show or hide flashcard details.
        </Typography>
      </Box>
    </Box>
  </DialogContent>
</Dialog>


      {/* snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <TagModal
        open={tagModalOpen}
        position={tagModalPosition}
        onClose={() => setTagModalOpen(false)}
        subfolderId={subfolder_id}
        cardId={cards[tagCardIndex]?.id}
        cardQuestionType={cards[tagCardIndex]?.question_type}
        onTagAdded={handleTagAdded}
        onTagUpdated={handleTagUpdated}
        onTagDeleted={handleTagDeleted}
        onShowSnackbar={showSnackbar}
      />
    </div>
  );
};

export default Flashcard;
